import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Calendar, Plus, Send, ChevronRight, Users, Trash2, Eye, Heart, MessageCircle, Bookmark, TrendingUp, Share2, ChevronLeft, Loader2, MessagesSquare } from 'lucide-react';
import { MediaThumb } from '../../shared/ui/MediaThumb';
import { type Publication } from '../../entities/publication';
import { publicationsApi } from '../../shared/api/publications.api';
import type { PaginationInfo } from '../../shared/api/publications.api';
import { toast } from 'sonner';
import { invalidateQuery, useCachedQuery } from '../../shared/lib/queryCache';
import { RefreshingHint } from '../../shared/ui/RefreshingHint';
import { ROUTES } from '../../shared/config/routes';
import { Skeleton } from '../../components/ui/skeleton';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { useRemembered } from '../../shared/lib/remembered';
import { useAccountStore } from '../../entities/account';
import { useAccountGroups } from '../../features/publish-groups';
import { JournalFiltersBar } from './JournalFiltersBar';
import {
  EMPTY_FILTERS, commentsLink, filterJournal, isFiltered, pageFromParam, runLimited, type JournalFilters,
} from './lib/journal';

// Группируем публикации по batchId
interface GroupedPublication {
  batchId: string | null;
  publications: Publication[];
  firstPublication: Publication;
  accountCount: number;
}

const ITEMS_PER_PAGE = 50;

/** Ключи кэша журнала: по префиксу сбрасываем все страницы разом. */
const PUBLICATIONS_CACHE_PREFIX = 'publications:page:';
/** Весь журнал — только когда включён фильтр: сервер фильтровать не умеет. */
const PUBLICATIONS_ALL_KEY = 'publications:all';

/** Метрики подтягиваются фоном не больше чем по стольку постов разом. */
const INSIGHTS_CONCURRENCY = 4;

const hasInsights = (p: Publication) =>
  !!p.insights && Object.values(p.insights).some((v) => typeof v === 'number' && v > 0);

export function PublicationsPage() {
  const navigate = useNavigate();
  // Страница — в адресе: «Назад» из публикации возвращает туда же, ссылкой можно поделиться.
  const [params, setParams] = useSearchParams();
  const currentPage = pageFromParam(params.get('page'));
  const setCurrentPage = (page: number) => {
    const next = new URLSearchParams(params);
    if (page <= 1) next.delete('page');
    else next.set('page', String(page));
    setParams(next);
  };
  const [filters, setFiltersRaw] = useRemembered<JournalFilters>('publications:journal-filters', EMPTY_FILTERS);
  const filtered = isFiltered(filters);
  const setFilters = (f: JournalFilters) => {
    setFiltersRaw(f);
    if (currentPage !== 1) setCurrentPage(1);
  };
  const groups = useAccountGroups();
  const accounts = useAccountStore((s) => s.accounts);

  const [refreshingInsights, setRefreshingInsights] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  // Свежие метрики поверх загруженной страницы — без перезагрузки всего списка.
  const [fresh, setFresh] = useState<Record<string, Publication['insights']>>({});

  // Страницу журнала, которую уже открывали, показываем сразу и обновляем в
  // фоне: листание туда-обратно не должно каждый раз упираться в спиннер.
  const pageQuery = useCachedQuery(
    `${PUBLICATIONS_CACHE_PREFIX}${currentPage}`,
    () => publicationsApi.getPaginated(currentPage, ITEMS_PER_PAGE),
    { enabled: !filtered },
  );
  // С фильтром — весь журнал разом (сервер отдаёт его кусками сам) и листаем у себя.
  const allQuery = useCachedQuery(PUBLICATIONS_ALL_KEY, () => publicationsApi.getAll(), { enabled: filtered });
  const query = filtered ? allQuery : pageQuery;

  useEffect(() => {
    if (query.error) toast.error('Ошибка загрузки публикаций');
  }, [query.error]);

  const filteredAll = useMemo(
    () => (filtered ? filterJournal(allQuery.data ?? [], filters, groups) : []),
    [filtered, allQuery.data, filters, groups],
  );
  const pagePublications: Publication[] = filtered
    ? filteredAll.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : pageQuery.data?.publications ?? [];
  const pagination: PaginationInfo | null = filtered
    ? { page: currentPage, limit: ITEMS_PER_PAGE, totalCount: filteredAll.length, totalPages: Math.max(1, Math.ceil(filteredAll.length / ITEMS_PER_PAGE)) }
    : pageQuery.data?.pagination ?? null;
  const isLoading = query.isLoading;

  /** Список изменился на сервере — забываем все страницы, не только текущую. */
  const reloadAfterChange = () => {
    invalidateQuery(PUBLICATIONS_CACHE_PREFIX);
    invalidateQuery(PUBLICATIONS_ALL_KEY);
    query.refresh();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const handleDelete = async (e: React.MouseEvent, group: GroupedPublication) => {
    e.stopPropagation();
    const many = group.accountCount > 1;
    const ok = await confirmDialog({
      title: many ? `Удалить ${group.accountCount} публикаций из журнала?` : 'Удалить из журнала публикаций?',
      description: many
        ? 'Записи исчезнут из журнала приложения. Посты останутся в Instagram — удалить их можно только в самом приложении Instagram.'
        : 'Запись исчезнет из журнала приложения. Пост останется в Instagram — удалить его можно только в самом приложении Instagram.',
      confirmText: 'Удалить из журнала',
      destructive: true,
    });
    if (!ok) return;

    const key = group.batchId || group.firstPublication.id;
    try {
      // Крутится только строка — весь список на время удаления не прячем.
      setDeletingKey(key);
      for (const pub of group.publications) {
        await publicationsApi.delete(pub.id);
      }
      toast.success(many ? `${group.accountCount} публикаций удалено из журнала` : 'Публикация удалена из журнала');
      reloadAfterChange();
    } catch (error) {
      console.error('Error deleting publications:', error);
      toast.error('Ошибка удаления публикации');
    } finally {
      setDeletingKey(null);
    }
  };

  /** Метрики страницы: по нескольку постов разом, каждая строка обновляется сама. */
  const loadInsights = async (pubs: Publication[]) => {
    let ok = 0;
    await runLimited(pubs, INSIGHTS_CONCURRENCY, async (pub) => {
      const updated = await publicationsApi.getInsights(pub.id);
      ok++;
      setFresh((prev) => ({ ...prev, [pub.id]: updated.insights }));
    });
    return ok;
  };

  // Фоном — для видимой страницы и только тем, у кого метрик ещё нет: кнопка
  // «Обновить метрики» остаётся для принудительного обновления всех.
  const autoLoaded = useRef(new Set<string>());
  useEffect(() => {
    const missing = pagePublications.filter((p) => !hasInsights(p) && !autoLoaded.current.has(p.id));
    if (!missing.length) return;
    missing.forEach((p) => autoLoaded.current.add(p.id));
    void loadInsights(missing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePublications.map((p) => p.id).join(',')]);

  const handleRefreshAllInsights = async () => {
    try {
      setRefreshingInsights(true);
      const ok = await loadInsights(pagePublications);
      invalidateQuery(PUBLICATIONS_CACHE_PREFIX);
      toast.success(`Обновлено ${ok} из ${pagePublications.length} публикаций`);
    } finally {
      setRefreshingInsights(false);
    }
  };

  // Подсчет суммарных метрик для группы
  const calculateTotalMetrics = (group: GroupedPublication) => {
    const totals = { views: 0, likes: 0, comments: 0, shares: 0, saves: 0, reach: 0 };
    group.publications.forEach((pub) => {
      const ins = fresh[pub.id] ?? pub.insights;
      if (ins) {
        totals.views += ins.views || 0;
        totals.likes += ins.likes || 0;
        totals.comments += ins.comments || 0;
        totals.shares += ins.shares || 0;
        totals.saves += ins.saves || 0;
        totals.reach += ins.reach || 0;
      }
    });
    return totals;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Группируем публикации (только в пределах страницы: склейка пакета через
  // границу страниц требует сервера).
  const groupedPublications: GroupedPublication[] = [];
  const processedBatchIds = new Set<string>();
  pagePublications.forEach((pub) => {
    if (pub.batchId && !processedBatchIds.has(pub.batchId)) {
      processedBatchIds.add(pub.batchId);
      const batchPublications = pagePublications.filter(p => p.batchId === pub.batchId);
      groupedPublications.push({
        batchId: pub.batchId,
        publications: batchPublications,
        firstPublication: batchPublications[0],
        accountCount: batchPublications.length,
      });
    } else if (!pub.batchId) {
      groupedPublications.push({ batchId: null, publications: [pub], firstPublication: pub, accountCount: 1 });
    }
  });

  const filtersBar = (
    <JournalFiltersBar value={filters} onChange={setFilters} accounts={accounts} groups={groups} />
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {filtersBar}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPages = pagination?.totalPages || 1;
  const totalCount = pagination?.totalCount || 0;

  return (
    <div className="space-y-6">
      {filtersBar}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>Отправлено через InstaFarm</CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-2">
                {totalCount === 0
                  ? (filtered ? 'По фильтру ничего не нашлось' : 'Нет публикаций')
                  : `${filtered ? 'По фильтру' : 'Всего публикаций'}: ${totalCount} (страница ${currentPage} из ${totalPages})`}
                <RefreshingHint active={query.isRefreshing} />
              </CardDescription>
            </div>
            {pagePublications.length > 0 && (
              <Button onClick={handleRefreshAllInsights} disabled={refreshingInsights} variant="outline" size="sm">
                {refreshingInsights ? <Loader2 className="size-4 animate-spin" /> : <TrendingUp className="size-4" />}
                {refreshingInsights ? 'Обновление...' : 'Обновить метрики'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {groupedPublications.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-4">
                {filtered ? 'Под фильтр не попала ни одна публикация.' : 'Публикации появятся здесь после создания постов'}
              </p>
              {filtered ? (
                <Button variant="outline" onClick={() => setFilters(EMPTY_FILTERS)}>Сбросить фильтр</Button>
              ) : (
                <Button onClick={() => navigate(ROUTES.PUBLICATION_NEW)}>
                  <Plus className="size-4" />
                  Новая публикация
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Превью</TableHead>
                    <TableHead>Аккаунты</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead>ER</TableHead>
                    <TableHead>Метрики</TableHead>
                    <TableHead className="w-[90px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedPublications.map((group) => {
                    const key = group.batchId || group.firstPublication.id;
                    const metrics = calculateTotalMetrics(group);
                    const hasMetrics = metrics.views > 0 || metrics.likes > 0 || metrics.comments > 0;
                    const denominator = metrics.views || metrics.reach || 0;
                    const er = denominator
                      ? (((metrics.likes + metrics.comments + metrics.shares + metrics.saves) / denominator) * 100).toFixed(2) + '%'
                      : null;
                    const comments = commentsLink(group.publications.map((p) => p.permalink));
                    const deleting = deletingKey === key;

                    return (
                      <TableRow
                        key={key}
                        className={`cursor-pointer hover:bg-muted/50 ${deleting ? 'opacity-50' : ''}`}
                        onClick={() => navigate(`/publications/${key}`)}
                      >
                        <TableCell>
                          <MediaThumb
                            url={group.firstPublication.mediaUrl}
                            mediaType={group.firstPublication.type === 'post' ? 'IMAGE' : 'VIDEO'}
                            className="w-14 h-14"
                            iconClassName="w-6 h-6"
                          />
                        </TableCell>
                        <TableCell>
                          {group.accountCount === 1 ? (
                            <div className="font-medium">{group.firstPublication.accountName}</div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{group.accountCount} аккаунтов</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {formatDate(group.firstPublication.publishedAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{er || '—'}</span>
                        </TableCell>
                        <TableCell>
                          {hasMetrics ? (
                            <div className="flex items-center gap-2 text-sm">
                              <div className="flex items-center gap-1 min-w-[52px]">
                                <Eye className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="tabular-nums">{formatNumber(metrics.views)}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-[42px]">
                                <Heart className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="tabular-nums">{formatNumber(metrics.likes)}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-[36px]">
                                <MessageCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="tabular-nums">{formatNumber(metrics.comments)}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-[36px]">
                                <Share2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="tabular-nums">{formatNumber(metrics.shares)}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-[36px]">
                                <Bookmark className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="tabular-nums">{formatNumber(metrics.saves)}</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {comments && (
                              <Button
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                title="Разобрать комментарии"
                                aria-label="Разобрать комментарии"
                                onClick={(e) => { e.stopPropagation(); navigate(comments); }}
                              >
                                <MessagesSquare className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Удалить из журнала"
                              aria-label="Удалить из журнала"
                              disabled={deleting}
                              onClick={(e) => void handleDelete(e, group)}
                            >
                              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1 || isLoading}
                  >
                    <ChevronLeft className="size-4" />
                    Предыдущая
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Страница {currentPage} из {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages || isLoading}
                  >
                    Следующая
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
